import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "opik_wishlist_guest";
const Ctx = createContext(null);

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); }
    catch { return new Set(); }
  });

  // Sync with backend when signed in
  useEffect(() => {
    if (user && user !== false) {
      api.get("/wishlist").then(({ data }) => {
        const remote = new Set(data.map((g) => g.id));
        // merge any guest ids into the server, then use server as truth
        const guest = Array.from(ids).filter((g) => !remote.has(g));
        Promise.all(guest.map((g) => api.post(`/wishlist/${g}`).catch(() => null)))
          .then(() => api.get("/wishlist"))
          .then(({ data: merged }) => {
            const merged_set = new Set(merged.map((g) => g.id));
            setIds(merged_set);
            localStorage.removeItem(STORAGE_KEY);
          });
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const persist = (next) => {
    if (!(user && user !== false)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(next)));
    }
  };

  const toggle = useCallback(async (gemId) => {
    const next = new Set(ids);
    const has = next.has(gemId);
    if (has) next.delete(gemId); else next.add(gemId);
    setIds(next);
    persist(next);
    if (user && user !== false) {
      try {
        if (has) await api.delete(`/wishlist/${gemId}`);
        else await api.post(`/wishlist/${gemId}`);
      } catch (e) { void e; }
    }
    return !has;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, user]);

  const has = (gemId) => ids.has(gemId);

  const value = useMemo(
    () => ({ ids: Array.from(ids), has, toggle, count: ids.size }),
    // `has` is reconstructed on every render but is a stable lookup over `ids`,
    // so we re-memo only when `ids` or `toggle` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids, toggle],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useWishlist = () => useContext(Ctx);
